// src/oura/components/client/StaffThread.tsx
//
// A question's conversation in the staff drawer, split in two (drawer specs
// v1.1, A0.7): the messages, read only, for the "Conversation" fold; and a
// controlled composer with no Send of its own, because the drawer's foot
// sends. Cmd or Ctrl+Enter sends too.
//
// The shared ItemThread (components/ItemThread.tsx) keeps its own API for
// the merchant's page; these are the staff drawer's pieces, in the house
// sizes (no bold, no bordered system lines, TYPE.tm timestamps).

import { useId, useRef, type ReactNode, type Ref } from "react";
import { CheckIcon, PaperclipIcon, PencilLineIcon, PhoneIcon, RefreshCwIcon, XIcon } from "lucide-react";
import { UPLOAD_ACCEPT, UPLOAD_MAX_BYTES, type Thread, type ThreadMessage } from "@service/aeo/clientReview";
import { cn } from "../../lib/cn";
import { FOCUS_RING, LATE_TEXT, PillButton, SURFACE, TYPE } from "@oura/ui";
import { ReplyPicturePicker, usePictureInput } from "../ReplyPictures";

/** "Tue 14:05", in the reader's time. */
function when(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ kind, name }: { kind: ThreadMessage["authorKind"]; name: string }) {
    const initials = name.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "?";
    return (
        <span
            aria-hidden
            className={cn(
                TYPE.tm,
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-semibold text-white",
                kind === "client" ? "bg-violet-600" : kind === "oura" ? "bg-gray-700" : "bg-blue-600",
            )}
        >
            {kind === "oura" ? "O" : initials}
        </span>
    );
}

const SYSTEM_ICON: Record<string, { icon: typeof CheckIcon; tone: string }> = {
    version: { icon: RefreshCwIcon, tone: "text-blue-600" },
    call_request: { icon: PhoneIcon, tone: "text-[#AF52DE]" },
    suggested_edit: { icon: PencilLineIcon, tone: "text-amber-600" },
    decision: { icon: CheckIcon, tone: "text-emerald-600" },
};

/** A thread's messages, oldest first, read only. Renders nothing for an empty thread. */
export function StaffThreadMessages({ thread }: { thread: Thread | null }) {
    const messages = thread?.messages ?? [];
    if (messages.length === 0) return null;
    return (
        <ol className="m-0 list-plain p-0">
            {messages.map((m) => {
                if (m.kind !== "message" && m.kind !== "suggested_edit") {
                    const s = SYSTEM_ICON[m.kind] ?? SYSTEM_ICON.decision;
                    const Icon = s.icon;
                    return (
                        <li key={m.id} className={cn(SURFACE.inner, TYPE.tm, "my-1.5 flex items-center gap-2 text-gray-600")}>
                            <Icon aria-hidden size={14} className={cn("shrink-0", s.tone)} />
                            <span className="min-w-0 flex-1 break-words">{m.body}</span>
                            <span className="shrink-0 tabular-nums text-gray-500">{when(m.at)}</span>
                        </li>
                    );
                }
                return (
                    <li key={m.id} className={cn("flex gap-2.5 border-t py-2.5 first:border-t-0 first:pt-0", SURFACE.hairline)}>
                        <Avatar kind={m.authorKind} name={m.authorName} />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className={cn(TYPE.tc, "font-medium text-gray-900")}>
                                    {m.authorName}
                                    {m.authorKind === "staff" ? ", ourteam" : ""}
                                </span>
                                <span className={cn(TYPE.tm, "shrink-0 tabular-nums text-gray-500")}>{when(m.at)}</span>
                            </div>
                            {m.body && <p className={cn(TYPE.tb, "m-0 mt-0.5 whitespace-pre-line break-words text-gray-700")}>{m.body}</p>}
                            {m.fileName && (
                                <span className={cn(TYPE.tm, SURFACE.inner, "mt-1.5 inline-flex items-center gap-1.5 py-1 text-gray-700")}>
                                    <PaperclipIcon aria-hidden size={12} className="text-gray-500" />
                                    {m.fileName}
                                </span>
                            )}
                            {m.suggestedEdit && (
                                <div className="mt-2 rounded-xl bg-blue-50 px-3 py-2.5">
                                    <p className={cn(TYPE.lab, "m-0")}>Their wording</p>
                                    <p className={cn(TYPE.tc, "m-0 mt-0.5 whitespace-pre-line text-gray-900")}>{m.suggestedEdit.after}</p>
                                    {m.suggestedEdit.accepted && (
                                        <p className={cn(TYPE.tm, "m-0 mt-1.5 inline-flex items-center gap-1 font-medium text-emerald-700")}>
                                            <CheckIcon aria-hidden size={12} />
                                            Accepted as the final wording
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

const FIELD = cn(
    TYPE.tb,
    "block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

/**
 * A reply field with no Send of its own: the drawer's foot sends. Cmd or
 * Ctrl+Enter calls `onSubmit`. `attach` adds a text button that picks one
 * file (checked against the upload limit); a failed send's words go in
 * `error`, under the field, in rose.
 */
export function StaffComposer({
    value,
    onChange,
    label,
    placeholder,
    hint,
    onSubmit,
    fieldRef,
    error,
    attach,
    pictures,
    above,
    rows = 4,
}: {
    value: string;
    onChange: (text: string) => void;
    /** The field's accessible name, e.g. "Reply to Siew Ling". */
    label: string;
    placeholder?: string;
    /** One quiet line under the field: "Emails from oura@ourteam.ai, signed Angel." */
    hint?: ReactNode;
    onSubmit: () => void;
    /** The drawer's focus claim, or a ref to focus the field by hand. */
    fieldRef?: Ref<HTMLTextAreaElement>;
    error?: string | null;
    attach?: { file: File | null; onFile: (file: File | null) => void; onError: (text: string) => void };
    /** Screenshots on the reply: picked, pasted or dropped on the field, shown as tiles under it. */
    pictures?: { files: File[]; onFiles: (files: File[]) => void; onError: (text: string) => void; disabled?: boolean };
    /** A text button above the field ("Draft with Oura"). */
    above?: ReactNode;
    rows?: number;
}) {
    const fieldId = useId();
    const hintId = useId();
    const errorId = useId();
    const fileInput = useRef<HTMLInputElement>(null);
    const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
    const drop = usePictureInput(pictures?.files ?? [], pictures?.onFiles ?? (() => {}), pictures?.onError ?? (() => {}), Boolean(pictures) && !pictures?.disabled);
    return (
        <div>
            <label htmlFor={fieldId} className="sr-only">
                {label}
            </label>
            {above && <div className="mb-1 flex min-h-6 items-center justify-end gap-2">{above}</div>}
            <textarea
                ref={fieldRef}
                id={fieldId}
                rows={rows}
                value={value}
                placeholder={placeholder}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
                {...drop.handlers}
                className={cn(FIELD, drop.over && "bg-blue-50 ring-2 ring-dashed ring-blue-600")}
            />
            {pictures && <ReplyPicturePicker files={pictures.files} onFiles={pictures.onFiles} onError={pictures.onError} disabled={pictures.disabled} />}
            {error && (
                <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                    {error}
                </p>
            )}
            {(hint || attach) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {hint && (
                        <p id={hintId} className={cn(TYPE.tc, "m-0 min-w-0 flex-1 text-gray-500")}>
                            {hint}
                        </p>
                    )}
                    {attach && (
                        <>
                            <input
                                ref={fileInput}
                                type="file"
                                hidden
                                accept={UPLOAD_ACCEPT}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = "";
                                    if (!f) return;
                                    if (f.size > UPLOAD_MAX_BYTES) {
                                        attach.onError("That file is over 20 MB.");
                                        return;
                                    }
                                    attach.onFile(f);
                                }}
                            />
                            {attach.file ? (
                                <span className={cn(TYPE.tc, "inline-flex min-w-0 items-center gap-1 text-gray-700")}>
                                    <PaperclipIcon aria-hidden size={12} className="shrink-0 text-gray-500" />
                                    <span className="max-w-[12rem] truncate" title={attach.file.name}>
                                        {attach.file.name}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => attach.onFile(null)}
                                        aria-label={`Remove ${attach.file.name}`}
                                        className={cn("relative grid h-6 w-6 place-items-center rounded-full text-gray-500 hover:bg-gray-100 after:absolute after:-inset-2.5 after:content-['']", FOCUS_RING)}
                                    >
                                        <XIcon aria-hidden size={12} />
                                    </button>
                                </span>
                            ) : (
                                <PillButton variant="text" icon={PaperclipIcon} onClick={() => fileInput.current?.click()}>
                                    Attach a file
                                </PillButton>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
